import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FALLBACK_PRICING } from "./pricingFallback";

// Fiyat ve indirim oranının TEK kaynağı: GET /api/pricing (admin ayarları / veritabanı).
// Frontend'de varsayılan fiyat veya varsayılan indirim oranı TUTULMAZ; böylece
// sayfa açılırken eski/yanlış değerler (ör. %10, 112,5 ₺) hiçbir zaman görünmez.

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

let cache = null; // { prices, discountRate } - son başarılı yanıt (sadece oturum içi, ilk boyama için)
let inflight = null;

export const fetchPricing = () => {
  if (inflight) return inflight;
  inflight = axios
    .get(`${BASE_URL}/api/pricing`, { params: { _: Date.now() } })
    .then((res) => {
      const rawPrices = res.data?.prices;
      const rate = Number(res.data?.discount_rate);
      if (!rawPrices || typeof rawPrices !== "object" || !Number.isFinite(rate)) {
        throw new Error("Geçersiz fiyat verisi");
      }
      const prices = {};
      Object.entries(rawPrices).forEach(([key, value]) => {
        const n = Number(value);
        if (Number.isFinite(n)) prices[key] = n;
      });
      cache = { prices, discountRate: rate };
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

/**
 * prices        : { makine: 150, ... } veya henüz yüklenmediyse null
 * discountRate  : yüzde (ör. 20) veya henüz yüklenmediyse null
 * loading       : ilk yükleme sürüyor mu
 * error         : yükleme hatası (true/false)
 * reload        : tekrar dene
 * Seçenek { useFallback: true } : yüklenene kadar yedek fiyatları gösterir.
 */
export const usePricing = ({ useFallback = false } = {}) => {
  // useFallback: API cevap verene kadar pricingFallback.js'deki yedek fiyatlar gösterilir
  // (sadece herkese açık sayfalar için; panellerde kullanılmaz).
  const [data, setData] = useState(cache ?? (useFallback ? FALLBACK_PRICING : null));
  const [loading, setLoading] = useState(!cache && !useFallback);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    if (!cache) setLoading(true);
    fetchPricing()
      .then((d) => setData(d))
      .catch((e) => {
        console.error("Fiyatlar yüklenemedi:", e);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(); // önbellek olsa bile her açılışta tazele
  }, [load]);

  return {
    prices: data?.prices ?? null,
    discountRate: data?.discountRate ?? null,
    loading,
    error: error && !data,
    ready: !!data,
    reload: load,
  };
};

// İndirimli birim fiyat (ondalıkları koruyarak; sadece gösterimde yuvarlanır)
export const applyDiscount = (price, discountRate) =>
  price * (1 - (discountRate ?? 0) / 100);

// 101.25 -> "101,25", 120 -> "120"
export const formatPrice = (n) =>
  Number.isFinite(n)
    ? new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(Math.round(n * 100) / 100)
    : "—";
